import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApolloClient } from "@apollo/client";
import * as tus from "tus-js-client";
import {
  CurrentPlatformQuery,
  useCurrentPlatformQuery,
} from "@/partner/entities/platforms";
import type { CurrentPlatformQueryType } from "@/types/graphql/partner.generated";
import {
  useRequestTusVideoUploadMutation,
  useRequestVideoUploadMutation,
} from "@/partner/entities/contents";
import { formatFileSize } from "@/shared/utils/formatFileSize";
import { buildTusOptions } from "./buildTusOptions";
import { MAX_UPLOAD_FILE_SIZE_BYTES } from "./uploadLimits";
import { readVideoFileDuration } from "./readVideoFileDuration";

export interface VideoUploadState {
  status: "idle" | "uploading" | "done" | "error";
  progress: number;
  videoUid: string | null;
  error: string | null;
  fileName: string | null;
  bytesUploaded: number;
  bytesTotal: number;
}

// 50 MiB. Кратно 256 KiB: требование Cloudflare. Bunny кратности не
// требует, поэтому значение общее для обеих веток.
const CHUNK_SIZE = 52428800;

const IDLE_STATE: VideoUploadState = {
  status: "idle",
  progress: 0,
  videoUid: null,
  error: null,
  fileName: null,
  bytesUploaded: 0,
  bytesTotal: 0,
};

export const useVideoUpload = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<VideoUploadState>(IDLE_STATE);
  const uploadRef = useRef<tus.Upload | null>(null);
  const apolloClient = useApolloClient();
  // Хук здесь нужен только чтобы держать запрос "тёплым" (кэш заполнен к
  // моменту start()): реальное значение провайдера читается ниже, внутри
  // start(), через client.query(), которое дожидается результата, а не
  // берёт возможно ещё не разрешившийся снимок из рендера.
  useCurrentPlatformQuery();
  const [requestVideoUpload] = useRequestVideoUploadMutation();
  const [requestTusVideoUpload] = useRequestTusVideoUploadMutation();

  const start = async (file: File) => {
    // Если уже идёт загрузка в этом же поле — обрываем её перед стартом новой
    // (повторный выбор файла заменяет предыдущую загрузку/значение).
    uploadRef.current?.abort(true);
    uploadRef.current = null;

    // См. uploadLimits.ts: файл сверх предела провайдера отклоняем сразу,
    // не тратя трафик и не упираясь в отказ где-то посреди закачки.
    if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      setState({
        status: "error",
        progress: 0,
        videoUid: null,
        error: t("videos.videoUpload.fileTooLargeError", {
          fileSize: formatFileSize(file.size),
          maxSize: formatFileSize(MAX_UPLOAD_FILE_SIZE_BYTES),
        }),
        fileName: file.name,
        bytesUploaded: 0,
        bytesTotal: file.size,
      });
      return;
    }

    setState({
      status: "uploading",
      progress: 0,
      videoUid: null,
      error: null,
      fileName: file.name,
      bytesUploaded: 0,
      bytesTotal: file.size,
    });

    try {
      // Провайдер платформы обязателен для выбора ветки ниже, а хук на
      // useCurrentPlatformQuery() выше может ещё не разрешиться (сразу
      // после монтирования страницы). Гадать на Cloudflare нельзя: на
      // Bunny-платформе requestVideoUpload бросает
      // VIDEO_UPLOAD_NOT_SUPPORTED без повтора. client.query() с
      // cache-first либо мгновенно отдаёт уже прогретый кэш, либо
      // дожидается того же запроса, что и хук.
      const { data: platformData } =
        await apolloClient.query<CurrentPlatformQueryType>({
          query: CurrentPlatformQuery,
          fetchPolicy: "cache-first",
        });

      // У провайдеров с прямым upload URL (Cloudflare) бэкенд отдаёт уже
      // созданный tus-ресурс, поэтому его адрес идёт в uploadUrl.
      // У провайдеров с подписанными заголовками (Bunny) ресурс создаёт
      // сам tus-клиент на общем endpoint, а авторизация лежит в headers.
      const isTusProvider =
        platformData?.currentPlatform?.effectiveVideoProvider === "BUNNY";

      let prepared: ReturnType<typeof buildTusOptions>;

      if (isTusProvider) {
        const { data } = await requestTusVideoUpload({
          variables: {
            fileName: file.name,
            fileSize: file.size,
            contentType: file.type || "video/mp4",
          },
        });
        const payload = data?.requestTusVideoUpload;
        if (!payload) {
          throw new Error(t("videos.videoUpload.noUploadUrlError"));
        }
        prepared = buildTusOptions({
          file,
          tus: {
            endpoint: String(payload.endpoint),
            videoUid: payload.videoUid,
            headers: payload.headers,
          },
        });
      } else {
        // Реальная длина файла: Cloudflare резервирует минуты хранения под
        // незавершённую загрузку по заявленной длительности, без неё бэкенд
        // заявляет потолок в часы, и параллельные загрузки упираются в
        // квоту плана (413 Storage capacity exceeded).
        const durationSeconds = await readVideoFileDuration(file);
        const { data } = await requestVideoUpload({
          variables: { fileSize: file.size, durationSeconds },
        });
        const payload = data?.requestVideoUpload;
        if (!payload) {
          throw new Error(t("videos.videoUpload.noUploadUrlError"));
        }
        prepared = buildTusOptions({
          file,
          direct: {
            uploadUrl: String(payload.uploadUrl),
            videoUid: payload.videoUid,
          },
        });
      }

      await new Promise<void>((resolve, reject) => {
        const upload = new tus.Upload(file, {
          ...prepared.options,
          chunkSize: CHUNK_SIZE,
          // Как в референсном useUpload.js — более терпеливый бэкофф важен
          // именно для крупных файлов на нестабильном канале.
          retryDelays: [0, 3000, 5000, 10000, 20000, 50000, 100000],
          onProgress: (bytesUploaded, bytesTotal) => {
            setState((prev) => ({
              ...prev,
              progress: Math.round((bytesUploaded / bytesTotal) * 100),
              bytesUploaded,
              bytesTotal,
            }));
          },
          onSuccess: () => resolve(),
          onError: (error) => reject(error),
        });
        uploadRef.current = upload;
        upload.start();
      });

      setState((prev) => ({
        ...prev,
        status: "done",
        progress: 100,
        videoUid: prepared.videoUid,
        error: null,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        status: "error",
        error:
          error instanceof Error
            ? error.message
            : t("videos.videoUpload.genericError"),
      }));
    }
  };

  const abort = () => {
    uploadRef.current?.abort(true);
    uploadRef.current = null;
    setState(IDLE_STATE);
  };

  return { state, start, abort };
};
